import React, { InputHTMLAttributes, useState } from 'react';
import Action from '../../Section/Action';

import { ReactComponent as VisibilityOff } from '../../../static/img/visibility-off.svg';
import { ReactComponent as VisibilityOn } from '../../../static/img/visibility-on.svg';
import * as Styled from './styles';
import Button from '../../Button';
import Message from '../../Message';
import alertIcon from '../../../static/img/alert-icon-gray-banner.svg';

export type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'intervention';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  placeHolderText?: string;
  type?: InputType;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  errorMessage?: string;
  disableMessage?: string | JSX.Element;
  width?: string;
  withButton?: boolean;
  withButtonAlternative?: boolean;
  onClick?: (e: any) => void;
  buttonText?: string | JSX.Element;
  buttonVariant?: 'primary' | 'secondary' | 'danger' | undefined;
  buttonDisabled?: boolean;
}

const Input = ({
  name,
  placeHolderText = '',
  errorMessage,
  disableMessage,
  onChange,
  type,
  width = '',
  withButton,
  withButtonAlternative,
  onClick,
  buttonText,
  buttonVariant,
  buttonDisabled,
  ...rest
}: Props) => {
  const [visible, setVisible] = useState<boolean>(false);

  if (type === 'password') {
    return (
      <>
        <Styled.InputPasswordContainer>
          <Styled.InputStyled
            onChange={onChange}
            id={name}
            name={name}
            placeholder={placeHolderText}
            type={visible ? 'text' : 'password'}
            errorMessage={errorMessage}
            width={width}
            aria-describedby={errorMessage && 'error-message'}
            {...rest}
          />
          <Styled.EyeIcon>
            <Action
              type="button"
              aria-pressed={visible}
              aria-label={visible ? 'Esconder senha' : 'Exibir senha'}
              onClick={() => setVisible(!visible)}
            >
              {visible ? <VisibilityOn /> : <VisibilityOff />}
            </Action>
          </Styled.EyeIcon>
        </Styled.InputPasswordContainer>
        {errorMessage && (
          <Styled.ErrorMessage id="error-message">
            {errorMessage}
          </Styled.ErrorMessage>
        )}
      </>
    );
  }

  if (withButtonAlternative) {
    return (
      <Styled.InputContainer>
        <Styled.InputStyled
          onChange={onChange}
          id={name}
          name={name}
          placeholder={placeHolderText}
          type={type}
          errorMessage={errorMessage}
          width={width}
          aria-describedby={errorMessage && 'error-message'}
          {...rest}
        />
        {errorMessage && <Message type="error">{errorMessage}</Message>}
        <Button
          variant={buttonVariant}
          disabled={buttonDisabled}
          onClick={onClick}
          width={width}
        >
          {buttonText}
        </Button>
      </Styled.InputContainer>
    );
  }

  if (withButton) {
    return (
      <>
        <Styled.InputBox>
          <Styled.InputStyled
            onChange={onChange}
            id={name}
            name={name}
            placeholder={placeHolderText}
            type={type}
            errorMessage={errorMessage}
            width={width}
            aria-describedby={errorMessage && 'error-message'}
            {...rest}
          />
          <Button
            variant={buttonVariant}
            disabled={buttonDisabled}
            onClick={onClick}
          >
            {buttonText}
          </Button>
        </Styled.InputBox>
        {errorMessage && (
          <Styled.ErrorMessage id="error-message">
            {errorMessage}
          </Styled.ErrorMessage>
        )}
      </>
    );
  }

  return (
    <>
      <Styled.InputStyled
        onChange={onChange}
        id={name}
        name={name}
        placeholder={placeHolderText}
        type={type}
        errorMessage={errorMessage}
        width={width}
        aria-describedby={errorMessage && 'error-message'}
        {...rest}
      />
      {errorMessage && (
        <Styled.ErrorMessage id="error-message">
          {errorMessage}
        </Styled.ErrorMessage>
      )}
      {disableMessage && (
        <Styled.DisableMessage id="disable-message">
          <Styled.IconAlert src={alertIcon} alt="Icone-de-alerta" />
          <span>{disableMessage}</span>
        </Styled.DisableMessage>
      )}
    </>
  );
};

export default Input;
